import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Car, CalendarCheck, Euro, ShieldCheck, Database, Trash, BarChart3 } from "lucide-react";
import { AdminDisputesPanel } from "@/components/AdminDisputesPanel";
import { AdminClaimsPanel } from "@/components/AdminClaimsPanel";
import { AdminContactPanel } from "@/components/AdminContactPanel";
import { FeatureFlagsPanel } from "@/components/FeatureFlagsPanel";
import { PartnerRequestsPanel } from "@/components/PartnerRequestsPanel";
import { PartnerPayoutsPanel } from "@/components/PartnerPayoutsPanel";
import { AdminPickupLocationsPanel } from "@/components/AdminPickupLocationsPanel";
import { OverviewPanel } from "@/components/admin/OverviewPanel";
import { LoueursTable } from "@/components/admin/LoueursTable";
import { AdminVehiclesGrid } from "@/components/admin/AdminVehiclesGrid";
import { AdminBookingsTable } from "@/components/admin/AdminBookingsTable";
import { SupportPanel } from "@/components/admin/SupportPanel";
import { TrustedDevicesPanel } from "@/components/admin/TrustedDevicesPanel";
import { EmailLogsPanel } from "@/components/admin/EmailLogsPanel";
import { SubadminsPanel } from "@/components/admin/SubadminsPanel";
import { useFlags } from "@/hooks/useFlags";
import { useAdminData } from "@/hooks/useAdminData";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TRIGGER_CLS = "rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold";

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const v2 = useFlags().v2_enabled;
  const isSuper = user?.role === "superadmin";
  const d = useAdminData(isSuper);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "overview");
  const emailParam = sp.get("email") || "";
  useEffect(() => { const tp = sp.get("tab"); if (tp) setTab(tp); }, [sp]);

  const statCards = [
    { icon: Building2, label: t("dash.loueurs"), value: d.stats?.loueurs ?? 0 },
    { icon: Car, label: t("dash.vehicles"), value: d.stats?.vehicles ?? 0 },
    { icon: CalendarCheck, label: t("dash.bookings"), value: d.stats?.bookings ?? 0 },
    { icon: Euro, label: t("dash.commissionStat"), value: `${d.stats?.platform_commission ?? 0} €` },
  ];

  return (
    <div className="section-sand min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-ink flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-canard" /> {t("dash.adminTitle")} · {isSuper ? t("dash.superadmin") : t("dash.subadmin")}</h1>
            <p className="text-muted2 font-body">{user?.name}</p>
          </div>
          {isSuper && (
            <div className="flex gap-2">
              <Button data-testid="seed-demo-button" variant="outline" onClick={d.seedDemo} className="rounded-full"><Database className="w-4 h-4 mr-1" /> {t("dash.demoData")}</Button>
              <Button data-testid="delete-demo-button" variant="outline" onClick={d.deleteDemo} className="rounded-full text-destructive border-destructive/30"><Trash className="w-4 h-4 mr-1" /> {t("dash.deleteDemo")}</Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 shadow-card border border-border">
              <s.icon className="w-6 h-6 text-canard mb-2" />
              <p className="text-2xl font-heading font-extrabold text-ink">{s.value}</p>
              <p className="text-xs text-muted2 font-semibold">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white rounded-full p-1 border border-border flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className={TRIGGER_CLS}><BarChart3 className="w-4 h-4 mr-1" /> {t("ov.tab")}</TabsTrigger>
            <TabsTrigger value="loueurs" data-testid="tab-loueurs" className={TRIGGER_CLS}>{t("dash.loueurs")}</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles" className={TRIGGER_CLS}>{t("dash.vehicles")}</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-adm-bookings" className={TRIGGER_CLS}>{t("dash.bookings")}</TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support" className={TRIGGER_CLS}>{t("dash.support")}</TabsTrigger>
            {v2 && <TabsTrigger value="litiges" data-testid="tab-adm-litiges" className={TRIGGER_CLS}>{t("litiges.tab")}</TabsTrigger>}
            <TabsTrigger value="claims" data-testid="tab-adm-claims" className={TRIGGER_CLS}>{t("claims.tab")}</TabsTrigger>
            <TabsTrigger value="devices" data-testid="tab-devices" className={TRIGGER_CLS}>{t("devices.tab")}</TabsTrigger>
            <TabsTrigger value="pickups" data-testid="tab-pickups" className={TRIGGER_CLS}>{t("pk.tab")}</TabsTrigger>
            {isSuper && <TabsTrigger value="subs" data-testid="tab-subs" className={TRIGGER_CLS}>{t("dash.subadmins")}</TabsTrigger>}
            {isSuper && <TabsTrigger value="versions" data-testid="tab-versions" className={TRIGGER_CLS}>{t("versions.tab")}</TabsTrigger>}
            {isSuper && <TabsTrigger value="partners" data-testid="tab-partners" className={TRIGGER_CLS}>{t("partnerAdmin.tab")}</TabsTrigger>}
            {isSuper && <TabsTrigger value="payouts" data-testid="tab-payouts" className={TRIGGER_CLS}>{t("payouts.tab")}</TabsTrigger>}
            {isSuper && <TabsTrigger value="emails" data-testid="tab-emails" className={TRIGGER_CLS}>Emails</TabsTrigger>}
            {isSuper && <TabsTrigger value="contact-info" data-testid="tab-contact-info" className={TRIGGER_CLS}>{t("contactInfo.tab")}</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className="mt-6"><OverviewPanel data={d.overview} onPeriod={d.loadOverview} /></TabsContent>
          <TabsContent value="loueurs" className="mt-6"><LoueursTable loueurs={d.loueurs} v2={v2} isSuper={isSuper} onToggle={d.toggleLoueur} onSetPlan={d.setLoueurPlan} /></TabsContent>
          <TabsContent value="vehicles" className="mt-6"><AdminVehiclesGrid vehicles={d.vehicles} onToggle={d.toggleVehicle} /></TabsContent>
          <TabsContent value="bookings" className="mt-6"><AdminBookingsTable bookings={d.bookings} /></TabsContent>
          <TabsContent value="support" className="mt-6"><SupportPanel contacts={d.contacts} /></TabsContent>
          <TabsContent value="litiges" className="mt-6"><AdminDisputesPanel /></TabsContent>
          <TabsContent value="claims" className="mt-6"><AdminClaimsPanel /></TabsContent>
          {isSuper && (
            <>
              <TabsContent value="versions" className="mt-6"><FeatureFlagsPanel /></TabsContent>
              <TabsContent value="partners" className="mt-6"><PartnerRequestsPanel /></TabsContent>
              <TabsContent value="payouts" className="mt-6"><PartnerPayoutsPanel /></TabsContent>
              <TabsContent value="emails" className="mt-6"><EmailLogsPanel logs={d.emailLogs} queue={d.emailQueue} onRetry={d.retryEmail} initialEmail={emailParam} /></TabsContent>
              <TabsContent value="contact-info" className="mt-6"><AdminContactPanel /></TabsContent>
            </>
          )}
          <TabsContent value="pickups" className="mt-6"><AdminPickupLocationsPanel /></TabsContent>
          <TabsContent value="devices" className="mt-6"><TrustedDevicesPanel devices={d.devices} onRevoke={d.revokeDevice} /></TabsContent>
          {isSuper && <TabsContent value="subs" className="mt-6"><SubadminsPanel subs={d.subs} perms={d.perms} onChanged={d.load} /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
}
